import React, {useCallback, useContext, useMemo} from 'react';
import {useParams} from 'react-router-dom';

import {WidthItem} from 'src/components/width-item';
import {ACCOUNT_TABS} from 'src/constants';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {useTabRedirect} from 'src/hooks/useTabRedirect';

import {FlatButton} from '@pb/kit-component-button';
import {DropdownMenu, DropdownMenuItem} from '@pb/kit-component-dropdown-menu';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import {LocaleContext} from '@pb/kit-context-locale';


import {getLicenseCaption} from './utils';

import {licenseSuggestActive} from './license-suggest.module.styl';


import type {LicenseInfo} from 'src/openapi/admin/models/LicenseInfo';
import type {LicenseShortInfo} from 'src/openapi/admin/models/LicenseShortInfo';


type Props = {
    history: Array<LicenseInfo> | null,
    activeSubTab: string,
};

export const LicenseSuggest: React.FC<Props> = ({history, activeSubTab}) => {
    const {licenseId, accountId} = useParams<{licenseId?: string, accountId?: string}>();
    const {locale} = useContext(LocaleContext);

    const activeLicense = useMemo(() => history?.find(({isActive}) => isActive), [history]);
    const {isFutureDate} = useDateTime();

    const futureLicenses = useMemo(
        () => history?.filter(({startDate}) => isFutureDate(startDate)),
        [history, isFutureDate],
    );

    const expiredLicenses = useMemo(
        () => history?.filter(({startDate, isActive}) => !isFutureDate(startDate) && !isActive),
        [history, isFutureDate],
    );

    const redirectToSubTab = useTabRedirect(`/profile/account/${accountId}/${ACCOUNT_TABS.LICENSE.name}/`);
    const onLicenseRedirect = useCallback((id: number) => {
        redirectToSubTab(`${id.toString()}/${activeSubTab}`);
    }, [redirectToSubTab, activeSubTab]);

    const anchorText = useMemo(() => {
        let selectedLicense: LicenseInfo | LicenseShortInfo | null =
            licenseId && history?.find(e => e.licenseId === +licenseId) || null;

        if (!selectedLicense) {
            selectedLicense = activeLicense && licenseId && activeLicense.licenseId === +licenseId
                ? activeLicense
                : null;
        }

        return selectedLicense ? getLicenseCaption(selectedLicense, locale.dateFormat) : '';
    }, [history, licenseId, activeLicense, locale.dateFormat]);

    return (
        <WidthItem width={200}>
            <Group justify="end">
                <DropdownMenu
                    hideOnBodyLeave={false}
                    anchor={({positionRef, getAnchorProps, anchorRef}) => (
                        <FlatButton
                            endIcon={<Icon type={Icons.ANGLE_DOWN} ref={positionRef}/>}
                            {...getAnchorProps()}
                            ref={anchorRef}>
                            <b>{anchorText}</b>
                        </FlatButton>
                    )}>
                    <div>
                        {futureLicenses?.length ? futureLicenses.map(license => (
                            <DropdownMenuItem
                                className={licenseId && license.licenseId === +licenseId
                                    ? licenseSuggestActive
                                    : undefined}
                                key={license.licenseId}
                                caption={getLicenseCaption(license, locale.dateFormat, 'Pending')}
                                onClick={() => {
                                    onLicenseRedirect(license.licenseId);
                                }}
                            />
                        )) : null}
                        {activeLicense ? [
                            <DropdownMenuItem
                                key={activeLicense.licenseId}
                                className={licenseId && activeLicense.licenseId === +licenseId ?
                                    licenseSuggestActive : undefined}
                                caption={getLicenseCaption(activeLicense, locale.dateFormat, 'Active')}
                                onClick={() => {
                                    if (activeLicense.licenseId) {
                                        onLicenseRedirect(activeLicense.licenseId);
                                    }
                                }}
                            />,
                        ] : []}
                        {expiredLicenses?.length ? expiredLicenses.map(license => (
                            <DropdownMenuItem

                                key={license.licenseId}
                                caption={getLicenseCaption(license, locale.dateFormat, 'Expired')}
                                className={licenseId && license.licenseId === +licenseId
                                    ? licenseSuggestActive
                                    : undefined}
                                onClick={() => {
                                    onLicenseRedirect(license.licenseId);
                                }}
                            />
                        )) : null}
                    </div>
                </DropdownMenu>
            </Group>
        </WidthItem>
    );
};
